import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Bell, Plus, Sun, Moon, Menu, Home, Compass, Bookmark, User, LogOut, Shield, LayoutDashboard, Store } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useClerk } from "@clerk/react";
import { useTheme } from "@/context/ThemeContext";
import { mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const links = [
  { to: "/explorar", label: "Explorar" },
  { to: "/explorar?sort=popular", label: "Populares" },
  { to: "/categorias", label: "Categorias" },
  { to: "/mercado", label: "Mercado" },
];

export function Navbar() {
  const { user, logout, notifCount } = useAuth();
  const clerk = useClerk();
  const fullLogout = async () => { await logout(); if (clerk.isSignedIn) await clerk.signOut(); };
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/explorar?q=${encodeURIComponent(q.trim())}`);
      setMobileOpen(false);
    }
  };

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center gap-4">
            <Logo />
            <nav className="hidden lg:flex items-center gap-1 ml-4">
              {links.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  {l.label}
                </Link>
              ))}
            </nav>

            <form onSubmit={submitSearch} className="ml-auto hidden md:block flex-1 max-w-sm">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="navbar-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar projetos, materiais..."
                  className="h-10 w-full rounded-full border border-input bg-secondary/60 pl-10 pr-4 text-sm outline-none transition-colors focus:border-primary focus:bg-card"
                />
              </div>
            </form>

            <div className="flex items-center gap-1.5 md:ml-0 ml-auto">
              <button
                data-testid="theme-toggle"
                onClick={toggle}
                className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                aria-label="Alternar tema"
              >
                {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>

              {user && (
                <Link
                  to="/notificacoes"
                  data-testid="nav-notifications"
                  className="relative flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  <Bell className="h-5 w-5" />
                  {notifCount > 0 && (
                    <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                      {notifCount > 9 ? "9+" : notifCount}
                    </span>
                  )}
                </Link>
              )}

              {user ? (
                <>
                  <Button
                    data-testid="nav-publish-btn"
                    onClick={() => navigate("/publicar")}
                    className="hidden sm:flex rounded-full gap-1.5 font-semibold"
                  >
                    <Plus className="h-4 w-4" /> Publicar
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid="nav-profile-menu" className="ml-1 rounded-full ring-2 ring-transparent transition hover:ring-primary/40">
                        <img src={mediaUrl(user.avatar)} alt={user.name} className="h-9 w-9 rounded-full object-cover" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>
                        <p className="font-semibold">{user.name}</p>
                        <p className="text-xs font-normal text-muted-foreground">@{user.username}</p>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate(`/u/${user.username}`)}>
                        <User className="mr-2 h-4 w-4" /> Meu perfil
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/painel")}>
                        <LayoutDashboard className="mr-2 h-4 w-4" /> Meu painel
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="menu-favorites" onClick={() => navigate("/favoritos")}>
                        <Bookmark className="mr-2 h-4 w-4" /> Meus favoritos
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="menu-market" onClick={() => navigate("/mercado/painel")}>
                        <Store className="mr-2 h-4 w-4" /> Compras e vendas
                      </DropdownMenuItem>
                      {user.role === "admin" && (
                        <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                          <Shield className="mr-2 h-4 w-4" /> Painel admin
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid="menu-logout" onClick={fullLogout} className="text-destructive focus:text-destructive">
                        <LogOut className="mr-2 h-4 w-4" /> Sair
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="hidden sm:flex items-center gap-2">
                  <Button variant="ghost" data-testid="nav-login-btn" onClick={() => navigate("/entrar")} className="rounded-full">
                    Entrar
                  </Button>
                  <Button data-testid="nav-register-btn" onClick={() => navigate("/cadastro")} className="rounded-full font-semibold">
                    Criar conta
                  </Button>
                </div>
              )}

              <button
                data-testid="mobile-menu-btn"
                onClick={() => setMobileOpen((o) => !o)}
                className="lg:hidden flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>

          {mobileOpen && (
            <div className="lg:hidden border-t border-border py-3 space-y-1">
              <form onSubmit={submitSearch} className="mb-2 md:hidden">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    data-testid="navbar-search-input-mobile"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscar..."
                    className="h-10 w-full rounded-full border border-input bg-secondary/60 pl-10 pr-4 text-sm outline-none"
                  />
                </div>
              </form>
              {links.map((l) => (
                <Link key={l.label} to={l.to} onClick={() => setMobileOpen(false)} className="block rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-secondary">
                  {l.label}
                </Link>
              ))}
              {!user && (
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1 rounded-full" onClick={() => { navigate("/entrar"); setMobileOpen(false); }}>Entrar</Button>
                  <Button className="flex-1 rounded-full" onClick={() => { navigate("/cadastro"); setMobileOpen(false); }}>Criar conta</Button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/90 glass lg:hidden">
        <div className="grid grid-cols-5">
          <MobileTab to="/" icon={Home} label="Início" active={location.pathname === "/"} />
          <MobileTab to="/explorar" icon={Compass} label="Explorar" active={location.pathname.startsWith("/explorar")} />
          <Link to={user ? "/publicar" : "/entrar"} data-testid="mobile-tab-create" className="flex flex-col items-center justify-center py-2">
            <span className="flex h-9 w-9 -mt-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
              <Plus className="h-5 w-5" />
            </span>
            <span className="mt-0.5 text-[10px] font-medium text-muted-foreground">Criar</span>
          </Link>
          <MobileTab to="/mercado" icon={Store} label="Mercado" active={location.pathname.startsWith("/mercado")} />
          <MobileTab to={user ? `/u/${user.username}` : "/entrar"} icon={User} label="Perfil" active={location.pathname.startsWith("/u/")} />
        </div>
      </nav>
    </>
  );
}

function MobileTab({ to, icon: Icon, label, active }) {
  return (
    <Link to={to} className="flex flex-col items-center justify-center py-2.5 gap-0.5">
      <Icon className={`h-5 w-5 ${active ? "text-primary" : "text-muted-foreground"}`} />
      <span className={`text-[10px] font-medium ${active ? "text-primary" : "text-muted-foreground"}`}>{label}</span>
    </Link>
  );
}
